// Enhanced src/features/workos/components/WorkOSCallback.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkOS } from '../context/WorkOSContext';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { logger } from '../../../utils/logger';

const WorkOSCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setIsAuthenticated } = useWorkOS();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  
  // Use a ref to track if we're already processing
  const processingRef = React.useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    let isProcessed = false;
    
    const processCallback = async () => {
      if (isProcessed) return;
      isProcessed = true;
      await handleCallback();
    };
    
    processCallback();
    
    // Cleanup function
    return () => {
      isProcessed = true;
    };
  }, []);

  const handleCallback = async () => {
    // Prevent multiple simultaneous processing
    if (processingRef.current) {
      logger.log('Already processing callback, skipping...');
      return;
    }
    processingRef.current = true;
    
    try {
      setIsProcessing(true);
      setError(null);
      
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      
      // Handle OAuth errors from WorkOS
      if (error) {
        let userFriendlyError = 'Authentication failed. Please try again.';
        
        switch (error) {
          case 'access_denied':
            userFriendlyError = 'You cancelled the login process. Click "Sign In" to try again.';
            break;
          case 'invalid_request':
            userFriendlyError = 'There was a problem with the login request. Please try again.';
            break;
          case 'server_error':
            userFriendlyError = 'The authentication service is temporarily unavailable. Please try again in a moment.';
            break;
          case 'temporarily_unavailable':
            userFriendlyError = 'The login service is busy. Please wait a moment and try again.';
            break;
          default:
            userFriendlyError = errorDescription || userFriendlyError;
        }
        
        throw new AuthError(userFriendlyError, 'oauth_error', false);
      }
      
      if (!code) {
        throw new AuthError(
          'No authorization code received. This might happen if you navigated here directly.',
          'missing_code',
          false
        );
      }

      // Check if this code was already processed (prevent replay attacks)
      const processedCodes = JSON.parse(localStorage.getItem('workos_processed_codes') || '[]');
      if (processedCodes.includes(code)) {
        logger.log('Authorization code already processed, checking for existing session...');
        
        const storedUser = localStorage.getItem('workos_user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          if (setUser && setIsAuthenticated) {
            setUser(userData);
            setIsAuthenticated(true);
          }
          const returnTo = state || localStorage.getItem('workos_return_to') || '/';
          localStorage.removeItem('workos_return_to');
          navigate(returnTo, { replace: true });
          return;
        } else {
          throw new AuthError(
            'This login link has already been used. Please sign in again.',
            'code_reused',
            false
          );
        }
      }

      logger.log('Processing WorkOS callback with code:', code);
      
      // UPDATED: Use unified-auth endpoint instead of workos-callback
      const functionUrl = import.meta.env.DEV 
        ? 'http://localhost:8888/.netlify/functions/unified-auth'
        : '/.netlify/functions/unified-auth';
      
      // Add retry logic for network requests
      const response = await fetchWithRetry(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          code, 
          state,
          source: 'web'  // ADDED: source identifier
        }),
      }, 3, 1000);

      if (!response.ok) {
        let errorMessage = 'Authentication failed. Please try again.';
        let errorType = 'network_error';
        let canRetry = true;
        
        try {
          const errorData = await response.json();
          
          // Handle specific WorkOS errors
          if (errorData.details) {
            if (errorData.details.includes('invalid_grant')) {
              // Code already used - check for existing session
              const storedUser = localStorage.getItem('workos_user');
              if (storedUser) {
                logger.log('Code already used, but user has valid session');
                const userData = JSON.parse(storedUser);
                if (setUser && setIsAuthenticated) {
                  setUser(userData);
                  setIsAuthenticated(true);
                }
                const returnTo = state || localStorage.getItem('workos_return_to') || '/';
                localStorage.removeItem('workos_return_to');
                navigate(returnTo, { replace: true });
                return;
              } else {
                errorMessage = 'This login link has expired. Please sign in again.';
                errorType = 'expired_code';
                canRetry = false;
              }
            } else if (errorData.details.includes('invalid_client')) {
              errorMessage = 'There\'s a configuration problem. Please contact support.';
              errorType = 'config_error';
              canRetry = false;
            } else if (errorData.details.includes('rate_limit')) {
              errorMessage = 'Too many login attempts. Please wait a moment and try again.';
              errorType = 'rate_limit';
              canRetry = true;
            }
          } else {
            // Use server error message if available
            errorMessage = errorData.error || errorMessage;
          }
        } catch (parseError) {
          logger.error('Error parsing error response:', parseError);
          
          // Handle different HTTP status codes
          switch (response.status) {
            case 400:
              errorMessage = 'Invalid login request. Please try signing in again.';
              canRetry = false;
              break;
            case 401:
              errorMessage = 'Authentication failed. Please sign in again.';
              canRetry = false;
              break;
            case 403:
              errorMessage = 'Access denied. Please contact support if this continues.';
              canRetry = false;
              break;
            case 429:
              errorMessage = 'Too many attempts. Please wait a moment and try again.';
              canRetry = true;
              break;
            case 500:
            case 502:
            case 503:
            case 504:
              errorMessage = 'The authentication service is temporarily unavailable. Please try again.';
              canRetry = true;
              break;
            default:
              errorMessage = 'Network error. Please check your connection and try again.';
              canRetry = true;
          }
        }
        
        throw new AuthError(errorMessage, errorType, canRetry);
      }

      const data = await response.json();
      
      // Store processed code to prevent reuse
      const newProcessedCodes = [...processedCodes, code].slice(-10); // Keep only last 10
      localStorage.setItem('workos_processed_codes', JSON.stringify(newProcessedCodes));
      
      if (setUser && setIsAuthenticated) {
        setUser(data.user);
        setIsAuthenticated(true);
      }
      
      localStorage.setItem('workos_user', JSON.stringify(data.user));
      
      const returnTo = state || localStorage.getItem('workos_return_to') || '/';
      localStorage.removeItem('workos_return_to');
      
      logger.log('WorkOS callback successful, redirecting to:', returnTo);
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      navigate(returnTo, { replace: true });
      
    } catch (err) {
      logger.error('WorkOS callback error:', err);
      
      // Clean up URL even on error
      window.history.replaceState({}, document.title, window.location.pathname);
      
      if (err instanceof AuthError) {
        setError({
          message: err.message,
          type: err.type,
          canRetry: err.canRetry
        });
      } else {
        // Handle unexpected errors
        setError({
          message: 'An unexpected error occurred during login. Please try again.',
          type: 'unexpected_error',
          canRetry: true
        });
      }
    } finally {
      setIsProcessing(false);
      processingRef.current = false;
    }
  };

  // Retry function for recoverable errors
  const handleRetry = () => {
    if (retryCount < 3) {
      setRetryCount(prev => prev + 1);
      setError(null);
      processingRef.current = false;
      handleCallback();
    } else {
      // Too many retries, redirect to login
      navigate('/', { replace: true });
    }
  };

  // Go back to login
  const handleBackToLogin = () => {
    // Clear any stored auth data
    localStorage.removeItem('workos_user');
    localStorage.removeItem('workos_return_to');
    navigate('/', { replace: true });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2 text-white">Authentication Error</h2>
            <p className="text-gray-300 mb-6">{error.message}</p>
          </div>
          
          <div className="space-y-3">
            {error.canRetry && retryCount < 3 ? (
              <button
                onClick={handleRetry}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Try Again {retryCount > 0 && `(${retryCount}/3)`}
              </button>
            ) : null}
            
            <button
              onClick={handleBackToLogin}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              Back to Sign In
            </button>
          </div>
          
          {error.type === 'config_error' && (
            <p className="text-sm text-gray-500 mt-4">
              Error code: {error.type}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <div className="text-center">
        <LoadingSpinner />
        <p className="mt-4 text-gray-300">
          {retryCount > 0 ? `Retrying authentication... (${retryCount}/3)` : 'Completing authentication...'}
        </p>
      </div>
    </div>
  );
};

// Custom error class for better error handling
class AuthError extends Error {
  constructor(message, type = 'unknown', canRetry = true) {
    super(message);
    this.name = 'AuthError';
    this.type = type;
    this.canRetry = canRetry;
  }
}

// Retry utility function
async function fetchWithRetry(url, options, maxRetries = 3, delay = 1000) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, {
        ...options,
        signal: AbortSignal.timeout(10000), // 10 second timeout
      });
      
      return response; // Return response for caller to handle status
      
    } catch (error) {
      logger.log(`Request attempt ${attempt}/${maxRetries} failed:`, error.message);
      
      if (attempt === maxRetries) {
        throw new AuthError(
          'Network connection failed. Please check your internet connection and try again.',
          'network_error',
          true
        );
      }
      
      // Wait before retrying, with exponential backoff
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }
}

export default WorkOSCallback;